/**
 * Vulnerability export configuration
 *
 * COMPLEXITY: Medium
 * - Status filter: Multiple options with "STARTS WITH" operator
 * - Severity filter: Priority-based with RiskTag styling
 * - 28 columns available
 * - Simple query (no relationships, direct node filters)
 *
 * MIGRATION FROM: src/sections/export/ExportVulnerabilities.tsx
 */

import {
  CheckCircleIcon,
  ClockIcon,
  ExclamationTriangleIcon,
  NoSymbolIcon,
} from '@heroicons/react/24/outline';
import React from 'react';

import type {
  ExportOption,
  UniversalExportConfig,
} from '@/components/export/universal/types';
import { GraphQueryBuilder } from '@/components/export/universal/utils';
import type { StatusChipVariant } from '@/components/ui/status-chip';
import type { Risk } from '@/types';
import {
  RiskPriority,
  RiskSeverity,
  RiskStatus,
  RiskStatusLabel,
} from '@/types';
import type { Severity } from '@/types/dropdown.types';
import { getStatusLabel } from '@/utils/vulnerabilityStatus.util';

/**
 * Status filter options
 * Maps display labels to backend status codes with StatusChip styling
 */
const STATUS_OPTIONS = [
  {
    label: RiskStatusLabel[RiskStatus.Triaged],
    value: RiskStatus.Triaged,
    variant: 'high' as StatusChipVariant,
    icon: React.createElement(ClockIcon, { className: 'size-3 shrink-0' }),
  },
  {
    label: RiskStatusLabel[RiskStatus.Opened],
    value: RiskStatus.Opened,
    variant: 'critical' as StatusChipVariant,
    icon: React.createElement(ExclamationTriangleIcon, {
      className: 'size-3 shrink-0',
    }),
  },
  {
    label: RiskStatusLabel[RiskStatus.Remediated],
    value: RiskStatus.Remediated,
    variant: 'info' as StatusChipVariant,
    icon: React.createElement(CheckCircleIcon, {
      className: 'size-3 shrink-0',
    }),
  },
  {
    label: RiskStatusLabel[RiskStatus.Accepted],
    value: RiskStatus.Accepted,
    variant: 'neutral' as StatusChipVariant,
    icon: React.createElement(NoSymbolIcon, { className: 'size-3 shrink-0' }),
  },
] as const;

/**
 * Severity filter options
 * Maps display labels to priority scores with severity tags
 */
const SEVERITY_OPTIONS = [
  {
    label: 'Critical',
    value: RiskPriority[RiskSeverity.Critical],
    severity: 'critical' as Severity,
  },
  {
    label: 'High',
    value: RiskPriority[RiskSeverity.High],
    severity: 'high' as Severity,
  },
  {
    label: 'Medium',
    value: RiskPriority[RiskSeverity.Medium],
    severity: 'medium' as Severity,
  },
  {
    label: 'Low',
    value: RiskPriority[RiskSeverity.Low],
    severity: 'low' as Severity,
  },
  {
    label: 'Info',
    value: RiskPriority[RiskSeverity.Info],
    severity: 'info' as Severity,
  },
  {
    label: 'Exposure',
    value: RiskPriority[RiskSeverity.Exposure],
    severity: 'exposure' as Severity,
  },
] as const;

/**
 * Column definitions - 28 columns total
 * Based on ExportVulnerabilities.tsx lines 21-60
 * Added: tags, risk_group (maps to Risk.DNS),
 *   login_target (normalized Risk.DNS, populated only for web-login-* risks)
 */
export const VULNERABILITY_COLUMNS = [
  { id: 'title', label: 'Title', defaultSelected: true },
  { id: 'risk_group', label: 'Risk Group', defaultSelected: true },
  { id: 'login_target', label: 'Login Target', defaultSelected: true },
  { id: 'severity', label: 'Severity', defaultSelected: true },
  { id: 'status', label: 'Status', defaultSelected: true },
  { id: 'tags', label: 'Tags', defaultSelected: true },
  { id: 'description', label: 'Description', defaultSelected: true },
  { id: 'impact', label: 'Impact', defaultSelected: true },
  { id: 'recommendation', label: 'Recommendation', defaultSelected: true },
  { id: 'references', label: 'References', defaultSelected: true },
  { id: 'evidence', label: 'Evidence', defaultSelected: true },
  {
    id: 'auxiliary_evidence',
    label: 'Auxiliary Evidence',
    defaultSelected: true,
  },
  { id: 'asset_name', label: 'Asset Name', defaultSelected: true },
  { id: 'asset_identifier', label: 'Asset Identifier', defaultSelected: true },
  { id: 'affected_asset', label: 'Affected Asset', defaultSelected: true },
  { id: 'cve', label: 'CVE', defaultSelected: true },
  { id: 'cisa_kev', label: 'CISA KEV', defaultSelected: true },
  { id: 'cvss_score', label: 'CVSS Score', defaultSelected: true },
  { id: 'epss_score', label: 'EPSS Score', defaultSelected: true },
  { id: 'exploits', label: 'Exploits', defaultSelected: true },
  { id: 'mitre_attack', label: 'MITRE ATT&CK', defaultSelected: true },
  { id: 'mitre_cwe', label: 'MITRE CWE', defaultSelected: true },
  { id: 'public_exploit', label: 'Public Exploit', defaultSelected: true },
  { id: 'threat_actors', label: 'Threat Actors', defaultSelected: true },
  { id: 'origins', label: 'Origins', defaultSelected: true },
  { id: 'notes', label: 'Notes', defaultSelected: true },
  { id: 'history', label: 'History', defaultSelected: true },
  { id: 'first_seen', label: 'First Seen', defaultSelected: true },
  { id: 'last_seen', label: 'Last Seen', defaultSelected: true },
  { id: 'guid', label: 'GUID', defaultSelected: true },
] as const;

/**
 * Base vulnerability export configuration. Internal — spread into the final
 * config by createVulnerabilityExportConfig (which applies status labels).
 */
const vulnerabilityExportConfig: UniversalExportConfig<Risk> = {
  entityType: 'Vulnerability',
  apiEntityType: 'risk',
  storageKey: 'ADVANCE_EXPORT_OPTIONS.vulnerabilities.v5',

  filterSections: [
    {
      id: 'status',
      label: 'Status',
      options: [...STATUS_OPTIONS],
      defaultSelected: [...STATUS_OPTIONS], // All status options checked by default
      showWhenSelected: false,
    },
    {
      id: 'severity',
      label: 'Severity',
      options: [...SEVERITY_OPTIONS],
      defaultSelected: [...SEVERITY_OPTIONS], // All severity options checked by default
      showWhenSelected: false,
      styleType: 'table', // Use table style for RiskTag display
    },
  ],

  columns: [...VULNERABILITY_COLUMNS],

  formats: [
    { label: 'CSV', value: 'csv' },
    { label: 'JSON', value: 'json' },
    { label: 'PDF', value: 'pdf' },
  ],
  defaultFormat: { label: 'CSV', value: 'csv' },

  /**
   * Build GraphQL query for vulnerability export
   *
   * QUERY STRUCTURE (from ExportVulnerabilities.tsx lines 184-220):
   * - Node: Risk
   * - Status filter: STARTS WITH operator
   * - Severity filter: Exact match on priority
   * - No relationships needed
   */
  buildQuery: (
    filters: Readonly<Record<string, readonly Readonly<ExportOption>[]>>,
    _selectedRows: readonly string[]
  ) => {
    let builder = GraphQueryBuilder.create(['Risk']);

    // Add status filter with STARTS WITH operator
    if (filters.status && filters.status.length > 0) {
      const statusValues = filters.status.map(s => s.value as string);
      builder = builder.filter('status', 'STARTS WITH', statusValues);
    }

    // Add severity filter (exact match on priority)
    if (filters.severity && filters.severity.length > 0) {
      const priorityValues = filters.severity.map(s => s.value as number);
      builder = builder.filter('priority', '=', priorityValues);
    }

    return builder.orderBy('priority', true).build();
  },

  /**
   * Validate export selections
   *
   * RULES:
   * - At least one column must be selected
   * - Max 100,000 rows (handled by default validation in UniversalExportModal)
   */
  validateExport: (
    _filters: Readonly<Record<string, readonly Readonly<ExportOption>[]>>,
    columns: readonly Readonly<ExportOption>[],
    _selectedRows: readonly string[]
  ) => {
    if (columns.length === 0) {
      return [
        {
          field: 'columns' as const,
          message: 'Please select at least one column to export.',
        },
      ];
    }
    return null;
  },
};

/**
 * Creates the vulnerability export configuration.
 *
 * @returns Export configuration
 */
export function createVulnerabilityExportConfig(): UniversalExportConfig<Risk> {
  const statusOptions = STATUS_OPTIONS.map(opt => ({
    ...opt,
    label: getStatusLabel(opt.value),
  }));

  return {
    ...vulnerabilityExportConfig,
    filterSections: [
      {
        id: 'status',
        label: 'Status',
        options: [...statusOptions],
        defaultSelected: [...statusOptions],
        showWhenSelected: false,
      },
      {
        id: 'severity',
        label: 'Severity',
        options: [...SEVERITY_OPTIONS],
        defaultSelected: [...SEVERITY_OPTIONS],
        showWhenSelected: false,
        styleType: 'table', // Use table style for RiskTag display
      },
    ],
    buildQuery: (
      filters: Readonly<Record<string, readonly Readonly<ExportOption>[]>>,
      _selectedRows: readonly string[]
    ) => {
      let builder = GraphQueryBuilder.create(['Risk']);

      if (filters.status && filters.status.length > 0) {
        const statusValues = filters.status.map(s => s.value as string);
        builder = builder.filter('status', 'STARTS WITH', statusValues);
      }

      // Add severity filter (unchanged)
      if (filters.severity && filters.severity.length > 0) {
        const priorityValues = filters.severity.map(s => s.value as number);
        builder = builder.filter('priority', '=', priorityValues);
      }

      return builder.orderBy('priority', true).build();
    },
  };
}
